import React from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface Panel {
  id: string;
  title: string;
  component: React.ReactNode;
}

interface SortablePanelProps {
  panel: Panel;
}

const SortablePanel: React.FC<SortablePanelProps> = ({ panel }) => {
  const { theme } = useTheme();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: panel.id });

  const style = {
    transform: transform ? CSS.Transform.toString({
      ...transform,
      x: 0, // Lock horizontal movement to 0
    }) : undefined,
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`
        ${panel.id === 'chart' || panel.id === 'live-price' ? 'theme-bg-primary' : panel.id === 'positions' ? 'theme-bg-secondary' : 'theme-bg-primary'}
        rounded-lg
        ${panel.id === 'positions' ? 'px-2 pt-0.5 pb-2 mt-1' : panel.id === 'markets' ? 'p-0 mt-0' : 'p-0'}
        relative
        ${panel.id === 'positions' ? 'h-full' : ''}
        ${isDragging ? 'opacity-75 shadow-lg' : ''}
        border
        ${theme === 'light' ? 'border-gray-200' : 'border-gray-800'}
      `}
    >
      {/* Drag Handle - Different positions for left vs right panel items */}
      {(panel.id !== 'chart' && panel.id !== 'positions') && (
        <div
          {...attributes}
          {...listeners}
          className={`absolute top-1/2 transform -translate-y-1/2 cursor-default z-10 ${
            // Different opacity for left panel vs right panel components
            ['orderbook', 'marketdepth', 'livepricebig', 'technical'].includes(panel.id) 
              ? 'opacity-50' // Right panel components - higher opacity
              : 'opacity-40' // Left panel components - slightly lower opacity
          } ${
            // Right panel components: orderbook, marketdepth, livepricebig, technical
            ['orderbook', 'marketdepth', 'livepricebig', 'technical'].includes(panel.id) 
              ? '-left-[0.75rem]' 
              : '-right-[0.90rem]'
          }`}
        >
          <div className="theme-text-quaternary hover:theme-text-secondary transition-colors">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path d="M7 2a2 2 0 1 1 .001 4.001A2 2 0 0 1 7 2zm0 6a2 2 0 1 1 .001 4.001A2 2 0 0 1 7 8zm0 6a2 2 0 1 1 .001 4.001A2 2 0 0 1 7 14z" />
            </svg>
          </div>
        </div>
      )}
      
      {/* Panel Content */}
      <div className="panel-content">
        {panel.component}
      </div>
    </div>
  );
};

export default SortablePanel;
